import moment from 'moment-timezone';

/**
 * Percentage of a 24-hour day the input minute count represents.
 * Always positive (uses Math.abs). Private helper — external callers
 * should use the two public helpers below.
 */
const minutesToPercentOfDay = (minutes) => {
	const minutesInDay = 24 * 60;
	return Math.abs((minutes / minutesInDay) * 100);
};

/**
 * How far past midnight a timestamp sits, expressed as a percentage of
 * the day. Used to horizontally position a chip on the timeline.
 * Returns 0 for timestamps before the day starts.
 */
export const getPersentageOffsetFromStartOfDay = (
	fromTimestamp,
	currentDay,
	timezone = moment.tz.guess()
) => {
	const currentMidnight = moment(currentDay).tz(timezone).startOf('day');
	const momentTimestamp = moment(fromTimestamp).tz(timezone);

	if (momentTimestamp.isBefore(currentMidnight)) {
		return 0;
	}

	const minutesFromMidnight = momentTimestamp.diff(currentMidnight, 'minutes');

	return minutesToPercentOfDay(minutesFromMidnight);
};

/**
 * Fraction of a day a `[from, to]` range covers *within* `currentDay`,
 * expressed as a percentage. Used to size chips on the timeline.
 *
 * Returns 100 when both endpoints fall outside `currentDay` (the range
 * fully spans the day). Otherwise clamps to the portion inside the day.
 */
export const getPeriodInPercentageLenghtOfDay = ({
	fromTimestamp,
	toTimestamp,
	currentDay,
	timeZone = moment.tz.guess(),
}) => {
	const tz = typeof timeZone === 'string' ? timeZone : timeZone.value;

	const momentFrom = moment(fromTimestamp).tz(tz);
	const momentTo = moment(toTimestamp).tz(tz);
	const momentCurrent = moment(currentDay).tz(tz);

	if (!momentFrom.isSame(momentCurrent, 'day') && !momentTo.isSame(momentCurrent, 'day')) {
		return 100;
	}
	if (momentTo.isAfter(momentCurrent.endOf('day'))) {
		const diffInMin = momentFrom.diff(momentCurrent.endOf('day'), 'minutes');
		return minutesToPercentOfDay(diffInMin);
	}
	if (momentFrom.isBefore(momentCurrent.startOf('day'))) {
		const diffInMin = momentTo.diff(momentCurrent.startOf('day'), 'minutes');
		return minutesToPercentOfDay(diffInMin);
	}
	const diffInMin = momentFrom.diff(momentTo, 'minutes');
	return minutesToPercentOfDay(diffInMin);
};
